/*
  ves-video-watch.js

  Storefront video-watch tracker (docs/strategy/STORE_VIDEO_INVENTORY_2026-09-29.md).
  Loaded from snippets/ves-video-watch.liquid (theme asset assets/ves-video-watch.js).
  Reports real watches of every video on the store: YouTube iframes (the VES video facade,
  the help-center lightbox), Vimeo iframes (none today), and HTML5 <video> (Shopify-hosted).
  Each event goes out through Shopify.analytics.publish('ves_video_watch', payload); the
  "VES capture" custom pixel (pixel/ves-pixel.js) picks it up and posts it to go/ /px with
  the visitor's anonymous id. This file sets no cookies, stores nothing, reads no personal
  data, and fails silent: every entry point is wrapped so a bug here can never break a page.

  What counts as a watch:
    - HTML5: playing UNMUTED, or started within 1.5 s of a click/keypress, or the shopper
      clicked the video itself. Muted loops that start on their own (pack-card hover clips,
      the "GIF" clips, tab clips on load) are decoration and are never reported. A video
      marked aria-hidden="true" counts only when unmuted or clicked.
    - YouTube / Vimeo: playing unmuted, or the iframe has focus (the shopper clicked into it).
    - Milestones play, 25, 50, 75, complete fire once each per video per page view, and only
      on natural playback: a seek does not credit skipped time, and 'complete' needs 75% (or
      60% of the runtime watched) so scrubbing to the end is not a completion.

  Videos added later (the facade swaps in an iframe on click, the help-center lightbox sets
  the src of an iframe that already exists, deferred-media appends a cloned <video>) are found
  by a MutationObserver (iframes) and by document-level capture listeners (media events do not
  bubble but do capture, so <video> needs no per-element binding, even 100+ hover clips).
  For a YouTube iframe without enablejsapi=1, the flag is added ONLY to an iframe seen being
  created or re-pointed (it has not started); an iframe that was already on the page is left
  alone, never reloaded. Cleaner fix: put &enablejsapi=1 in the src the two scripts build
  (assets/ves-video-facade.js and assets/ves-help-center.js), see the how-to-apply doc.

  Tested by pixel/test/ves-video-watch.test.mjs, which loads THIS file in a vm sandbox.
*/
(function (root) {
  "use strict";

  var MILESTONES = [25, 50, 75];
  var NATURAL_MAX_DELTA = 3;      // seconds between two samples that still count as playing
  var GESTURE_WINDOW_MS = 1500;   // a play() this soon after a click/keypress is user-started
  var POLL_MS = 500;
  var EVENT_NAME = "ves_video_watch";

  // ------------------------------------------------------------------ pure helpers
  function capStr(v, max) {
    var s = v === null || v === undefined ? "" : String(v);
    return s.length > max ? s.slice(0, max) : s;
  }

  // Milestone tracker for ONE video. sample(pos, dur, eligible) is fed on every tick while
  // the video is playing; it returns the events to emit (each {action, seconds_watched,
  // duration}). Position samples only earn credit when they follow the previous sample by a
  // small forward step (natural playback); a jump is a seek and just re-baselines.
  function createTracker(loop) {
    var prev = null, seconds = 0, started = false, fired = {};
    function mk(action, dur) {
      fired[action] = 1;
      return {
        action: action,
        seconds_watched: Math.round(seconds),
        duration: (typeof dur === "number" && isFinite(dur) && dur > 0) ? Math.round(dur) : 0
      };
    }
    function completeAllowed(dur) {
      return !!(fired["75"] || (dur > 0 && isFinite(dur) && seconds >= 0.6 * dur));
    }
    return {
      gap: function () { prev = null; },
      seconds: function () { return seconds; },
      sample: function (pos, dur, eligible) {
        var res = [];
        if (typeof pos !== "number" || !isFinite(pos) || pos < 0) return res;
        if (!eligible) { prev = pos; return res; }
        var validDur = typeof dur === "number" && isFinite(dur) && dur > 0;
        if (!started) {
          started = true; prev = pos;
          res.push(mk("play", dur));
          return res;
        }
        if (prev === null) { prev = pos; return res; }
        var delta = pos - prev, i, m;
        if (delta > 0 && delta <= NATURAL_MAX_DELTA) {
          seconds += delta;
          if (validDur) {
            for (i = 0; i < MILESTONES.length; i++) {
              m = MILESTONES[i];
              if (!fired[m] && (prev / dur) * 100 < m && (pos / dur) * 100 >= m) res.push(mk(String(m), dur));
            }
          }
        } else if (loop && validDur && delta < 0 && prev / dur >= 0.85 && pos / dur <= 0.15) {
          if (!fired.complete && completeAllowed(dur)) res.push(mk("complete", dur));
        }
        prev = pos;
        return res;
      },
      ended: function (dur) {
        prev = null;
        if (!started || fired.complete || !completeAllowed(dur)) return [];
        return [mk("complete", dur)];
      }
    };
  }

  function ytId(src) {
    var m = /^(?:https?:)?\/\/(?:www\.)?youtube(?:-nocookie)?\.com\/embed\/([A-Za-z0-9_-]{6,20})(?:[/?#]|$)/i.exec(String(src || ""));
    return m ? m[1] : null;
  }
  function vimeoId(src) {
    var m = /^(?:https?:)?\/\/player\.vimeo\.com\/video\/(\d{3,15})(?:[/?#]|$)/i.exec(String(src || ""));
    return m ? m[1] : null;
  }
  function hasJsApi(src) { return /[?&]enablejsapi=1(?:&|#|$)/.test(String(src || "")); }
  function withJsApi(src, origin) {
    var s = String(src || ""), hash = "", i = s.indexOf("#");
    if (i >= 0) { hash = s.slice(i); s = s.slice(0, i); }
    if (!hasJsApi(s)) s += (s.indexOf("?") >= 0 ? "&" : "?") + "enablejsapi=1";
    if (origin && !/[?&]origin=/.test(s)) s += "&origin=" + encodeURIComponent(origin);
    return s + hash;
  }

  // Shopify-hosted video key: the media id, i.e. the basename up to the first dot. The same
  // video is served as many renditions (<id>.HD-1080p-7.2Mbps-94594029.mp4, <id>.m3u8), so the
  // full basename would split one video into several.
  function shopifyKey(url) {
    var s = String(url || "").split("#")[0].split("?")[0];
    if (!s) return "";
    var base = s.slice(s.lastIndexOf("/") + 1);
    try { base = decodeURIComponent(base); } catch (e) {}
    var dot = base.indexOf(".");
    if (dot > 0) base = base.slice(0, dot);
    return capStr(base.replace(/[^A-Za-z0-9_-]/g, ""), 120);
  }

  function productHandle(pathname) {
    var m = /(?:^|\/)products\/([A-Za-z0-9_-]+)/.exec(String(pathname || ""));
    return m ? m[1].toLowerCase() : "";
  }

  function trimLabel(s) {
    return capStr(String(s || "").replace(/\s+/g, " ").replace(/^\s+|\s+$/g, ""), 60);
  }

  function buildPayload(o) {
    var page = capStr(String(o.pathname || "").split("?")[0].split("#")[0], 200);
    var p = {
      v: 1,
      video_key: capStr(o.key, 120),
      provider: o.provider,
      action: o.action,
      seconds_watched: Math.max(0, Math.round(o.seconds_watched || 0)),
      duration: Math.max(0, Math.round(o.duration || 0)),
      page: page
    };
    var handle = productHandle(page);
    if (handle) p.product = handle;
    var sec = trimLabel(o.section);
    if (sec) p.section = sec;
    return p;
  }

  // ------------------------------------------------------------------ install (DOM glue)
  // env: { window, document, MutationObserver, setInterval, clearInterval, setTimeout, now }
  function install(env) {
    var win = env.window, doc = env.document;
    var now = env.now || function () { return Date.now(); };
    var setIv = env.setInterval || win.setInterval.bind(win);
    var clearIv = env.clearInterval || win.clearInterval.bind(win);
    var setTo = env.setTimeout || win.setTimeout.bind(win);

    var sent = {};                  // provider|key|action -> 1  (once per video per page view)
    var lastGesture = null;
    var queue = [], flushTimer = null, flushTries = 0;
    var entries = [];               // iframe entries, for tests / disposal
    var ytState = null;             // null | "loading" | "ready"
    var ytWaiters = [];
    var vimeoState = null, vimeoWaiters = [];

    // ---- publish ----
    function tryPublish(p) {
      try {
        var S = win.Shopify;
        if (S && S.analytics && typeof S.analytics.publish === "function") {
          S.analytics.publish(EVENT_NAME, p);
          return true;
        }
      } catch (e) { return true; }   // a throwing publish is dropped, never retried in a loop
      return false;
    }
    function flush() {
      flushTimer = null;
      var pending = queue; queue = [];
      for (var i = 0; i < pending.length; i++) {
        if (!tryPublish(pending[i])) queue.push(pending[i]);
      }
      if (queue.length && ++flushTries < 6) flushTimer = setTo(flush, 1500);
    }
    function publish(p) {
      if (tryPublish(p)) return;
      if (queue.length < 30) queue.push(p);
      if (!flushTimer) flushTimer = setTo(flush, 1500);
    }

    function sectionLabel(el) {
      try {
        if (!el || !el.closest) return "";
        var s = el.closest("[data-section-type]");
        if (s && s.getAttribute("data-section-type")) return s.getAttribute("data-section-type");
        var w = el.closest(".shopify-section");
        if (w) {
          var cls = String(w.getAttribute("class") || "").split(/\s+/), i;
          for (i = 0; i < cls.length; i++) {
            if (/^ves-.+-section$/.test(cls[i])) return cls[i].slice(0, -8);   // ves-demo-video
          }
          var id = String(w.getAttribute("id") || "").replace(/^shopify-section-/, "");
          id = id.replace(/^[a-z]+--\d+__/i, "");
          if (id) return id;
        }
        var box = el.closest("section") || el.closest("main") || null;
        var h = box && box.querySelector ? box.querySelector("h1,h2,h3") : null;
        return h ? trimLabel(h.textContent) : "";
      } catch (e) { return ""; }
    }

    function emit(entry, r) {
      try {
        var k = entry.provider + "|" + entry.key + "|" + r.action;
        if (!entry.key || sent[k]) return;
        sent[k] = 1;
        publish(buildPayload({
          key: entry.key, provider: entry.provider, action: r.action,
          seconds_watched: r.seconds_watched, duration: r.duration,
          pathname: win.location && win.location.pathname,
          section: sectionLabel(entry.el)
        }));
      } catch (e) {}
    }
    function emitAll(entry, list) { for (var i = 0; i < list.length; i++) emit(entry, list[i]); }

    // ---------------- HTML5 <video> (document-level capture listeners) ----------------
    function videoUrl(v) {
      var u = v.currentSrc || (v.getAttribute && v.getAttribute("src")) || (v.getAttribute && v.getAttribute("data-src")) || "";
      if (!u && v.querySelector) {
        var s = v.querySelector("source");
        u = s ? (s.getAttribute("src") || "") : "";
      }
      return u;
    }
    function videoEntry(v) {
      var e = v.__vesWatch;
      if (!e) {
        e = { el: v, provider: "html5", key: "", tracker: null, userStarted: false, interacted: false };
        try { v.__vesWatch = e; } catch (x) {}
      }
      var key = shopifyKey(videoUrl(v));
      if (key !== e.key || !e.tracker) {          // first sight, or the element got a new src
        e.key = key;
        e.tracker = createTracker(!!v.loop);
        e.userStarted = false;
      }
      return e;
    }
    function html5Eligible(e, v) {
      if (!v.muted && v.volume > 0) return true;
      if (e.interacted) return true;
      if (v.getAttribute && v.getAttribute("aria-hidden") === "true") return false;
      return !!e.userStarted;
    }
    function onMedia(ev) {
      try {
        var v = ev && ev.target;
        if (!v || v.tagName !== "VIDEO") return;
        var type = ev.type, e = videoEntry(v);
        if (!e.key) return;
        if (type === "play" && lastGesture !== null && now() - lastGesture <= GESTURE_WINDOW_MS) e.userStarted = true;
        if (type === "pause" || type === "seeking" || type === "waiting" || type === "emptied" || type === "loadstart") {
          e.tracker.gap();
          return;
        }
        if (type === "ended") {
          if (html5Eligible(e, v)) emitAll(e, e.tracker.ended(v.duration));
          return;
        }
        if (v.paused) return;
        emitAll(e, e.tracker.sample(v.currentTime, v.duration, html5Eligible(e, v)));
      } catch (x) {}
    }
    function onClick(ev) {
      try {
        lastGesture = now();
        var t = ev && ev.target;
        if (t && t.tagName === "VIDEO") { var e = videoEntry(t); e.interacted = true; }
      } catch (x) {}
    }
    function onKey() { lastGesture = now(); }

    var mediaEvents = ["play", "playing", "timeupdate", "pause", "seeking", "waiting", "ended", "volumechange", "emptied", "loadstart"];
    for (var mi = 0; mi < mediaEvents.length; mi++) doc.addEventListener(mediaEvents[mi], onMedia, true);
    doc.addEventListener("click", onClick, true);
    doc.addEventListener("keydown", onKey, true);

    // ---------------- iframes (YouTube, Vimeo) ----------------
    function loadScript(src, onload) {
      try {
        var s = doc.createElement("script");
        s.src = src; s.async = true;
        if (onload) s.onload = onload;
        (doc.head || doc.documentElement).appendChild(s);
      } catch (e) {}
    }
    function whenYT(cb) {
      if (win.YT && win.YT.Player) { cb(); return; }
      ytWaiters.push(cb);
      if (ytState) return;
      ytState = "loading";
      var prior = win.onYouTubeIframeAPIReady;
      win.onYouTubeIframeAPIReady = function () {
        try { if (typeof prior === "function") prior(); } catch (e) {}
        ytState = "ready";
        var w = ytWaiters; ytWaiters = [];
        for (var i = 0; i < w.length; i++) { try { w[i](); } catch (e2) {} }
      };
      loadScript("https://www.youtube.com/iframe_api");
    }
    function whenVimeo(cb) {
      if (win.Vimeo && win.Vimeo.Player) { cb(); return; }
      vimeoWaiters.push(cb);
      if (vimeoState) return;
      vimeoState = "loading";
      loadScript("https://player.vimeo.com/api/player.js", function () {
        vimeoState = "ready";
        var w = vimeoWaiters; vimeoWaiters = [];
        for (var i = 0; i < w.length; i++) { try { w[i](); } catch (e) {} }
      });
    }

    function stopPoll(e) { if (e.timer) { clearIv(e.timer); e.timer = null; } }
    function dispose(e) {
      stopPoll(e);
      e.dead = true;
      try { if (e.player && typeof e.player.destroy === "function" && e.provider === "youtube") e.player.destroy(); } catch (x) {}
    }
    function ytTick(e) {
      if (e.dead) return;
      try {
        var p = e.player;
        var eligible = !p.isMuted() || doc.activeElement === e.el;
        emitAll(e, e.tracker.sample(p.getCurrentTime(), p.getDuration(), eligible));
      } catch (x) {}
    }
    function startYouTube(e) {
      whenYT(function () {
        if (e.dead) return;
        try {
          if (!e.el.id) e.el.id = "ves-yt-" + entries.length;
          e.player = new win.YT.Player(e.el, {
            events: {
              onStateChange: function (ev) {
                if (e.dead) return;
                var st = ev && ev.data;
                if (st === 1) {                                   // playing
                  ytTick(e);
                  if (!e.timer) e.timer = setIv(function () { ytTick(e); }, POLL_MS);
                } else if (st === 0) {                            // ended
                  ytTick(e);
                  stopPoll(e);
                  try { emitAll(e, e.tracker.ended(e.player.getDuration())); } catch (x) {}
                } else if (st === 2 || st === 3) {                // paused / buffering
                  stopPoll(e); e.tracker.gap();
                }
              }
            }
          });
        } catch (x) {}
      });
    }
    function startVimeo(e) {
      whenVimeo(function () {
        if (e.dead) return;
        try {
          var p = new win.Vimeo.Player(e.el), srcMuted = /[?&]muted=1/.test(e.src), unmuted = false;
          e.player = p;
          var feed = function (data) {
            try {
              var eligible = unmuted || !srcMuted || doc.activeElement === e.el;
              emitAll(e, e.tracker.sample(data.seconds, data.duration, eligible));
            } catch (x) {}
          };
          p.on("play", function (d) { if (d) feed(d); });
          p.on("timeupdate", feed);
          p.on("volumechange", function (d) { if (d && d.volume > 0) unmuted = true; });
          p.on("pause", function () { e.tracker.gap(); });
          p.on("seeked", function () { e.tracker.gap(); });
          p.on("ended", function (d) { try { emitAll(e, e.tracker.ended(d && d.duration)); } catch (x) {} });
        } catch (x) {}
      });
    }

    // isNew = seen being created / re-pointed by the observer (has not started); false for
    // an iframe found by the initial scan (may already be playing: never touched).
    function syncIframe(el, isNew) {
      try {
        var src = el.getAttribute("src") || "";
        var old = null, i;
        for (i = 0; i < entries.length; i++) if (entries[i].el === el && !entries[i].dead) { old = entries[i]; break; }
        if (old && old.src === src) return;
        if (old) dispose(old);
        var yid = ytId(src), vid = yid ? null : vimeoId(src);
        if (!yid && !vid) return;
        var e = { el: el, provider: yid ? "youtube" : "vimeo", key: yid || vid, src: src, tracker: createTracker(false) };
        if (yid && !hasJsApi(src)) {
          if (!isNew) return;                        // already on the page: never reload it
          e.src = withJsApi(src, win.location && win.location.origin);
          entries.push(e);
          el.setAttribute("src", e.src);             // entry is registered first, so the observer ignores this write
        } else {
          entries.push(e);
        }
        if (yid) startYouTube(e); else startVimeo(e);
      } catch (x) {}
    }

    function scanNode(node, isNew) {
      try {
        if (!node || node.nodeType !== 1) return;
        if (node.tagName === "IFRAME") syncIframe(node, isNew);
        if (node.querySelectorAll) {
          var list = node.querySelectorAll("iframe"), i;
          for (i = 0; i < list.length; i++) syncIframe(list[i], isNew);
        }
      } catch (x) {}
    }

    var MO = env.MutationObserver;
    if (MO) {
      try {
        var mo = new MO(function (records) {
          try {
            for (var i = 0; i < records.length; i++) {
              var r = records[i];
              if (r.type === "childList") {
                for (var j = 0; j < r.addedNodes.length; j++) scanNode(r.addedNodes[j], true);
              } else if (r.type === "attributes" && r.target && r.target.tagName === "IFRAME") {
                syncIframe(r.target, true);
              }
            }
          } catch (x) {}
        });
        mo.observe(doc.documentElement || doc, { childList: true, subtree: true, attributes: true, attributeFilter: ["src"] });
      } catch (x) {}
    }
    scanNode(doc.documentElement || doc.body, false);

    return { sent: sent, entries: entries, flush: flush, _lastGesture: function () { return lastGesture; } };
  }

  var api = {
    createTracker: createTracker, ytId: ytId, vimeoId: vimeoId, hasJsApi: hasJsApi, withJsApi: withJsApi,
    shopifyKey: shopifyKey, productHandle: productHandle, trimLabel: trimLabel, buildPayload: buildPayload,
    install: install, EVENT_NAME: EVENT_NAME
  };

  if (typeof module !== "undefined" && module && module.exports) {
    module.exports = api;
    return;
  }
  try {
    if (!root || root.__vesVideoWatch) return;
    root.__vesVideoWatch = true;
    var go = function () {
      try {
        install({ window: root, document: root.document, MutationObserver: root.MutationObserver });
      } catch (e) {}
    };
    if (root.document.readyState === "loading") root.document.addEventListener("DOMContentLoaded", go);
    else go();
  } catch (e) {}
})(typeof window !== "undefined" ? window : this);
